/* eslint-disable @typescript-eslint/no-unused-vars */
import { MessageType } from '@doveit/bricks';
import { formatEuro } from '@doveit/hammer';
import {
  GoalMetric, PerformanceMetric, PerformanceRating, TimeRange,
} from '../../../../providers/api/dtos';

const metricTranslations: Record<GoalMetric, {
  singular: string,
  plural: string
}> = {
  rumors: {
    singular: 'notizia',
    plural: 'notizie',
  },
  prospects: {
    singular: 'valutazione',
    plural: 'valutazioni',
  },
  assignments: {
    singular: 'mandato',
    plural: 'mandati',
  },
  sales: {
    singular: 'vendita',
    plural: 'vendite',
  },
};

const defaultMessageByStatusAndMetric: Record<PerformanceRating, {
  type: MessageType,
  text: Record<GoalMetric, {
    title: string,
    // todo: update the description when we introduce the learngin platform and make this required
    description?: string
  }>
}
> = {
  ABOVE_EXPECTATIONS: {
    type: 'success',
    text: {
      rumors: {
        title: 'Ottimo lavoro: sei avanti rispetto al piano',
        description: 'Potresti chiudere l\'obiettivo in anticipo ed andare al di là delle iniziali aspettative. Continua così!',
      },
      prospects: {
        title: 'Ottimo lavoro: sei avanti rispetto al piano',
        description: 'Potresti chiudere l\'obiettivo in anticipo ed andare al di là delle iniziali aspettative. Continua così!',
      },
      assignments: {
        title: 'Ottimo lavoro: sei avanti rispetto al piano',
        description: 'Potresti chiudere l\'obiettivo in anticipo ed andare al di là delle iniziali aspettative. Continua così!',
      },
      sales: {
        title: 'Ottimo lavoro: sei avanti rispetto al piano',
        description: 'Potresti chiudere l\'obiettivo in anticipo ed andare al di là delle iniziali aspettative. Continua così!',
      },
    },
  },
  ON_TRACK: {
    type: 'info',
    text: {
      rumors: {
        title: 'Continua così: sei in linea con il piano',
        description: 'Potrai raggiungere l\'obiettivo annuale con successo mantenendo questo ritmo.',
      },
      prospects: {
        title: 'Continua così: sei in linea con il piano',
        description: 'Potrai raggiungere l\'obiettivo annuale con successo mantenendo questo ritmo.',
      },
      assignments: {
        title: 'Continua così: sei in linea con il piano',
        description: 'Potrai raggiungere l\'obiettivo annuale con successo mantenendo questo ritmo.',
      },
      sales: {
        title: 'Continua così: sei in linea con il piano',
        description: 'Potrai raggiungere l\'obiettivo annuale con successo mantenendo questo ritmo.',
      },
    },
  },
  NEARLY_ON_TRACK: {
    type: 'info',
    text: {
      rumors: {
        title: 'Continua così: basta un piccolo sforzo in più',
        description: 'Potrai raggiungere l\'obiettivo annuale con successo aumentando di poco la tua media.',
      },
      prospects: {
        title: 'Continua così: basta un piccolo sforzo in più',
        description: 'Potrai raggiungere l\'obiettivo annuale con successo aumentando di poco la tua media.',
      },
      assignments: {
        title: 'Continua così: basta un piccolo sforzo in più',
        description: 'Potrai raggiungere l\'obiettivo annuale con successo aumentando di poco la tua media.',
      },
      sales: {
        title: 'Continua così: basta un piccolo sforzo in più',
        description: 'Potrai raggiungere l\'obiettivo annuale con successo aumentando di poco la tua media.',
      },
    },
  },
  BELOW_EXPECTATIONS: {
    type: 'warning',
    text: {
      rumors: {
        title: 'La situazione è a rischio: serve costanza e attenzione',
      },
      prospects: {
        title: 'La situazione è a rischio: serve costanza e attenzione',
      },
      assignments: {
        title: 'La situazione è a rischio: serve costanza e attenzione',
      },
      sales: {
        title: 'La situazione è a rischio: serve costanza e attenzione',
      },
    },
  },
  CRITICAL: {
    type: 'critical',
    text: {
      rumors: {
        title: 'La situazione è critica: serve un cambio di marcia immediato',
      },
      prospects: {
        title: 'La situazione è critica: serve un cambio di marcia immediato',
      },
      assignments: {
        title: 'La situazione è critica: serve un cambio di marcia immediato',
      },
      sales: {
        title: 'La situazione è critica: serve un cambio di marcia immediato',
      },
    },
  },
};

export function getPerformanceMessage({
  target,
  metric,
  metricData,
  salesMetric,
}: {
  target: number,
  metric: GoalMetric,
  metricData: Record<TimeRange, PerformanceMetric>,
  salesMetric: Record<TimeRange, PerformanceMetric>,
}) {
  const metricStatus = metricData.yearly.status;
  const salesStatus = salesMetric.yearly.status;
  const metricYearlyProgress = metricData.yearly.progress;
  const salesYearlyProgress = salesMetric.yearly.progress;
  const salesYearlyExpected = salesMetric.yearly.expected;
  const salesYearlyPerformance = salesMetric.yearly.performance;
  const translations = metricTranslations[metric];

  const defaultMessageByStatus = defaultMessageByStatusAndMetric[metricStatus];
  const defaultMessageTextsByMetric = defaultMessageByStatus.text[metric];

  const message = {
    title: defaultMessageTextsByMetric.title,
    description: defaultMessageTextsByMetric.description,
    type: defaultMessageByStatus.type,
    // todo: put the action config here
  };

  switch (true) {
    case salesYearlyProgress >= 1 && metricYearlyProgress >= 1 && salesStatus === 'ABOVE_EXPECTATIONS' && metricStatus === 'ABOVE_EXPECTATIONS': {
      const potentialNewTarget = Math.round(target * (salesYearlyPerformance / salesYearlyExpected));

      message.title = 'Fantastico: hai raggiunto l\'obiettivo di guadagno';
      message.description = `Mantenendo questo ritmo potresti raggiungere un obiettivo di guadagno pari a ${formatEuro(potentialNewTarget)}.`;
      break;
    }
    case salesYearlyProgress >= 1 && metricYearlyProgress < 1 && salesStatus === 'ABOVE_EXPECTATIONS' && metricStatus !== 'ABOVE_EXPECTATIONS':
      message.title = 'Potresti fare ancora di più';
      message.description = 'Pur avendo raggiunto il tuo obiettivo di guadagno, migliorando questa metrica potresti guadagnare ancora di più.';
      break;
    case salesYearlyProgress < 1 && metricYearlyProgress >= 1 && salesStatus === 'ABOVE_EXPECTATIONS' && metricStatus === 'ABOVE_EXPECTATIONS':
      message.title = `Fantastico: hai raggiunto l'obiettivo di ${translations.plural}`;
      message.description = 'Potresti raggiungere l\'obiettivo di guadagno in anticipo e andare al di là delle iniziali aspettative. Continua così!';
      break;
    case salesYearlyProgress < 1 && metricYearlyProgress >= 1 && salesStatus !== 'ABOVE_EXPECTATIONS' && metricStatus === 'ABOVE_EXPECTATIONS':
      message.title = `Fantastico: hai raggiunto l'obiettivo di ${translations.plural}`;
      message.description = 'Lavora sulle vendite per raggiungere il tuo obiettivo di guadagno.';
      break;
    default:
      break;
  }

  return message;
}
